import {
  MutableRefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SidePanelProps } from 'app/shared/components/SidePanel/model';

import {
  RESIZER_WIDTH,
  SIDE_PANEL_DEFAULT_WIDTH,
  SIDE_PANEL_MENU_WIDTH,
  SIDE_PANEL_MIN_WIDTH,
} from 'app/shared/components/SidePanel/constants';

import { debounce } from 'lodash';
import { selectSidebarOpen } from 'app/modules/sidebar/selectors';
import { setPanelWidths } from 'app/shared/components/SidePanel/sliceSidePanel';
import { useDispatch, useSelector } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';
import styled, { css } from 'styled-components';

import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconChevronsLeft,
  IconChevronsRight,
} from '@u21/tabler-icons';
import {
  getPanelElement,
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import { List, Paper } from '@mui/material';
import { SidePanelMenuItem } from 'app/shared/components/SidePanel/SidePanelMenuItem';
import {
  U21Button,
  U21Spacer,
  U21Typography,
} from 'app/shared/u21-ui/components';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { useTheme } from '@mui/material/styles';
import { findScrollableParent } from 'app/shared/utils/react';
import { isCmdCtrlClick } from 'app/shared/utils/keyboard';
import {
  SidePanelInstance,
  SidePanelInstanceContext,
} from 'app/shared/components/SidePanel/contexts';

export const SIDEPANEL_SEARCH_PARAM_KEY = 'sidepanel';
export const EXPAND_SIDEPANEL_SEARCH_PARAM_KEY = 'expandSidePanel';

export const SidePanelLayout = (props: SidePanelProps) => {
  const {
    children,
    ignoreSidebar,
    list,
    localStorageKey,
    mainPanelProps,
    sidePanelInstanceRef,
    sidePanelProps,
  } = props;

  const history = useHistory();
  const { pathname, search } = useLocation();
  const theme = useTheme();

  const dispatch = useDispatch();
  const sidebarOpenState = useSelector(selectSidebarOpen);
  const sidebarOpen = ignoreSidebar ? false : sidebarOpenState;

  const mainPanelID = `main-panel-${localStorageKey}`;
  const sidePanelID = `side-panel-${localStorageKey}`;

  const parsed = useMemo(() => queryString.parse(search), [search]);
  const selectedKey = parsed[SIDEPANEL_SEARCH_PARAM_KEY];

  const [dragging, setDragging] = useState(false);
  const [mainPanelCollapsed, setMainPanelCollapsed] = useState(false);
  const [sidePanelCollapsed, setSidePanelCollapsed] = useState(false);

  const selected = useMemo(
    () => list.find((i) => i.label === selectedKey) ?? list[0],
    [list, selectedKey],
  );

  const mainPanelRef: MutableRefObject<ImperativePanelHandle | null> =
    useRef(null);
  const sidePanelRef: MutableRefObject<ImperativePanelHandle | null> =
    useRef(null);

  const { ref: containerRef } = useResizeDetector({
    refreshMode: 'debounce',
    refreshRate: 300,
  });

  // calculate width as a percentage
  // use ref offsetWidth over width since the debounce causes initialization to be delayed
  const containerWidth = containerRef.current?.offsetWidth || Infinity;

  // resizer is not included in the percentage so account for half of resizer in percentages
  const sidePanelMinSize = Math.min(
    ((SIDE_PANEL_MIN_WIDTH + RESIZER_WIDTH / 2) / containerWidth) * 100,
    50,
  );
  const sidePanelDefaultSize = Math.min(
    ((SIDE_PANEL_DEFAULT_WIDTH + RESIZER_WIDTH / 2) / containerWidth) * 100,
    50,
  );
  const sidePanelCollapsedSize = (SIDE_PANEL_MENU_WIDTH / containerWidth) * 100;

  // no longer expandable if container width is less than 2x of SIDE_PANEL_MIN_WIDTH
  const sideBySideEnabled =
    containerWidth - RESIZER_WIDTH > 2 * SIDE_PANEL_MIN_WIDTH;

  useEffect(() => {
    if (list.length > 0) {
      // if no url param or param not valid, set to first element in list
      if (!selectedKey || !list.find((i) => i.label === selectedKey)) {
        history.replace({
          search: `?${queryString.stringify({ ...parsed, [SIDEPANEL_SEARCH_PARAM_KEY]: list[0].label })}`,
        });
      }
    }
  }, [history, list, parsed, selectedKey]);

  const sideBySideEnabledRef = useRef(sideBySideEnabled);
  sideBySideEnabledRef.current = sideBySideEnabled;
  const sidePanelInstance = useMemo<SidePanelInstance>(
    () => ({
      collapseSidePanel: () => {
        sidePanelRef.current?.collapse();
        if (!sideBySideEnabledRef.current) {
          mainPanelRef.current?.expand();
        }
      },
      expandSidePanel: () => {
        sidePanelRef.current?.expand();
        if (!sideBySideEnabledRef.current) {
          mainPanelRef.current?.collapse();
        }
      },
      collapseMainPanel: () => {
        mainPanelRef.current?.collapse();
        if (!sideBySideEnabledRef.current) {
          sidePanelRef.current?.expand();
        }
      },
      expandMainPanel: () => {
        mainPanelRef.current?.expand();
        if (!sideBySideEnabledRef.current) {
          sidePanelRef.current?.collapse();
        }
      },
      isSidePanelCollapsed: () => sidePanelCollapsed,
      isMainPanelCollapsed: () => mainPanelCollapsed,
    }),
    [sidePanelCollapsed, mainPanelCollapsed],
  );

  useImperativeHandle(
    sidePanelInstanceRef,
    () => sidePanelInstance,
    [sidePanelInstance],
  );

  useEffect(() => {
    if (parsed[EXPAND_SIDEPANEL_SEARCH_PARAM_KEY] !== 'true') {
      return undefined;
    }

    let outerFrame = 0;
    let innerFrame = 0;

    outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => {
        sidePanelInstance.collapseMainPanel();
        sidePanelInstance.expandSidePanel();

        const rest = { ...parsed };
        delete rest[EXPAND_SIDEPANEL_SEARCH_PARAM_KEY];
        history.replace({
          pathname,
          search: queryString.stringify(rest),
        });
      });
    });

    return () => {
      cancelAnimationFrame(outerFrame);
      cancelAnimationFrame(innerFrame);
    };
  }, [history, pathname, parsed, sidePanelInstance]);

  useEffect(() => {
    if (!sideBySideEnabled && !mainPanelRef.current?.isCollapsed()) {
      sidePanelRef.current?.collapse();
    }
  }, [sideBySideEnabled]);

  const [sidePanelReady, setSidePanelReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const scrollElement = findScrollableParent(containerRef.current);
    if (!scrollElement) {
      return undefined;
    }

    let pendingRAFID: number | undefined;

    // this function here dynamically calculates the available space inside the scroll container's viewport area
    // and updates the sidepanel height to the maximum allowable viewport space
    const updateSidePanelHeight = () => {
      const sidePanel = getPanelElement(sidePanelID);
      if (!sidePanel) {
        return;
      }

      // Cancel any pending frame before scheduling a new one
      if (pendingRAFID) {
        cancelAnimationFrame(pendingRAFID);
      }

      pendingRAFID = requestAnimationFrame(() => {
        const scrollElementRect = scrollElement.getBoundingClientRect();
        const scrollElementComputed = getComputedStyle(scrollElement);
        const scrollElementPaddingTop = parseInt(
          scrollElementComputed.paddingTop,
          10,
        );
        const scrollElementPaddingBottom = parseInt(
          scrollElementComputed.paddingBottom,
          10,
        );

        // height of the visible scrollable space minus padding
        const scrollElementInnerHeight =
          scrollElement.offsetHeight -
          scrollElementPaddingTop -
          scrollElementPaddingBottom;

        // sidepanel top coordinate accounting for scroll
        const sidePanelTop =
          containerRef.current.getBoundingClientRect().top +
          scrollElement.scrollTop;

        // height of whatever's on top of sidepanel calculated by sidepanel top - scrollElement top
        const aboveSidePanelHeight = sidePanelTop - scrollElementRect.top;

        // expand the height during scroll up to aboveSidePanelHeight
        if (scrollElement.scrollTop < aboveSidePanelHeight) {
          sidePanel.style.height = `${scrollElementInnerHeight - aboveSidePanelHeight + scrollElement.scrollTop}px`;
        } else {
          sidePanel.style.height = `${scrollElementInnerHeight}px`;
        }
      });
    };

    const resizeObserver = new ResizeObserver(updateSidePanelHeight);
    resizeObserver.observe(containerRef.current);
    resizeObserver.observe(scrollElement);

    scrollElement.addEventListener('scroll', updateSidePanelHeight, {
      passive: true,
    });

    return () => {
      resizeObserver.disconnect();
      scrollElement.removeEventListener('scroll', updateSidePanelHeight);
      // Cancel any pending animation frame
      if (pendingRAFID) {
        cancelAnimationFrame(pendingRAFID);
      }
    };
  }, [containerRef, sidePanelID, sidePanelReady]);

  const mainPanelRefCallback = useCallback(
    (node: ImperativePanelHandle | null) => {
      const mainPanel = getPanelElement(mainPanelID);
      if (mainPanel) {
        // set transition after mount so that the panel does not jump
        requestAnimationFrame(() => {
          mainPanel.style.transition = theme.transitions.create(['flex-grow']);
        });
      }
      mainPanelRef.current = node;
    },
    [mainPanelID, theme],
  );

  const sidePanelRefCallback = useCallback(
    (node: ImperativePanelHandle | null) => {
      const sidePanel = getPanelElement(sidePanelID);
      if (sidePanel) {
        // set transition after mount so that the panel does not jump
        requestAnimationFrame(() => {
          sidePanel.style.transition = theme.transitions.create(['flex-grow']);
        });
      }
      sidePanelRef.current = node;
      setSidePanelReady(true);
    },
    [sidePanelID, theme],
  );

  return (
    <SidePanelInstanceContext.Provider value={sidePanelInstance}>
      <Container ref={containerRef}>
        {/* render only when containerRef is defined since the width calculations need to be ready first */}
        {/* don't care about this in tests because it is not collapsible */}
        {(containerRef.current || process.env.NODE_ENV === 'test') && (
          <PanelGroup
            autoSaveId={localStorageKey}
            direction="horizontal"
            // overwrite overflow inline style so sticky works
            style={{ overflow: 'visible' }}
            onLayout={debounce(
              ([mainPanelPercentage, sidePanelPercentage]: number[]) => {
                const mainPanelWidth = Math.round(
                  (mainPanelPercentage / 100) * containerWidth -
                    RESIZER_WIDTH / 2,
                );
                const sidePanelWidth = Math.round(
                  (sidePanelPercentage / 100) * containerWidth -
                    RESIZER_WIDTH / 2,
                );
                dispatch(setPanelWidths({ mainPanelWidth, sidePanelWidth }));
              },
              300,
            )}
          >
            <Panel
              // tests are collapsing the panel by default so turn off as a workaround
              collapsible={process.env.NODE_ENV !== 'test'}
              id={mainPanelID}
              minSize={50}
              order={1}
              ref={mainPanelRefCallback}
              {...mainPanelProps}
              onExpand={() => {
                setMainPanelCollapsed(false);
                mainPanelProps?.onExpand?.();
              }}
              onCollapse={() => {
                setMainPanelCollapsed(true);
                mainPanelProps?.onCollapse?.();
              }}
            >
              {children}
            </Panel>
            {list.length > 0 && (
              <>
                {/* PanelResizeHandle causes the UI to not be clickable so don't render in tests */}
                {!sidebarOpen && process.env.NODE_ENV !== 'test' && (
                  <StyledPanelResizeHandle
                    $dragging={dragging}
                    disabled={!sideBySideEnabled}
                    onDragging={(newDragging) => {
                      setDragging(newDragging);
                      const mainPanel = getPanelElement(mainPanelID);
                      const sidePanel = getPanelElement(sidePanelID);
                      if (newDragging) {
                        // remove transition when manually dragging since it interferes with the resize responsiveness
                        if (mainPanel) {
                          mainPanel.style.transition = 'none';
                        }
                        if (sidePanel) {
                          sidePanel.style.transition = 'none';
                        }
                      } else {
                        // add transition back when dragging ends
                        if (mainPanel) {
                          mainPanel.style.transition = theme.transitions.create(
                            ['flex-grow'],
                          );
                        }
                        if (sidePanel) {
                          sidePanel.style.transition = theme.transitions.create(
                            ['flex-grow'],
                          );
                        }
                      }
                    }}
                  >
                    <Resizer />
                  </StyledPanelResizeHandle>
                )}
                <SidePanel
                  $sidebarOpen={sidebarOpen}
                  // tests are collapsing the panel by default so turn off as a workaround
                  collapsible={process.env.NODE_ENV !== 'test'}
                  collapsedSize={sidePanelCollapsedSize}
                  defaultSize={sidePanelDefaultSize}
                  id={sidePanelID}
                  minSize={sidePanelMinSize}
                  order={2}
                  ref={sidePanelRefCallback}
                  {...sidePanelProps}
                  onExpand={() => {
                    setSidePanelCollapsed(false);
                    sidePanelProps?.onExpand?.();
                  }}
                  onCollapse={() => {
                    setSidePanelCollapsed(true);
                    sidePanelProps?.onCollapse?.();
                  }}
                >
                  <SidePanelContainer square $sidebarOpen={sidebarOpen}>
                    <SidePanelHeader>
                      <CollapseButtonContainer>
                        {sidePanelCollapsed ? (
                          <U21Button
                            aria-label="Expand"
                            icon={<IconChevronsLeft />}
                            onClick={() => {
                              if (sideBySideEnabled) {
                                sidePanelRef.current?.expand();
                                mainPanelRef.current?.expand();
                              } else {
                                sidePanelRef.current?.expand();
                                mainPanelRef.current?.collapse();
                              }
                            }}
                          />
                        ) : (
                          <U21Button
                            aria-label="Collapse"
                            icon={<IconChevronsRight />}
                            onClick={() => {
                              if (sideBySideEnabled) {
                                if (mainPanelCollapsed) {
                                  sidePanelRef.current?.expand();
                                  mainPanelRef.current?.expand();
                                } else {
                                  sidePanelRef.current?.collapse();
                                  mainPanelRef.current?.expand();
                                }
                              } else {
                                mainPanelRef.current?.expand();
                                sidePanelRef.current?.collapse();
                              }
                            }}
                          />
                        )}
                      </CollapseButtonContainer>
                      {!sidePanelCollapsed && (
                        <SidePanelHeaderContent horizontal useFlexGap>
                          {selected.header || (
                            <U21Typography variant="subtitle1">
                              {selected.label}
                            </U21Typography>
                          )}
                          <SidePanelActionContainer>
                            {selected.action}
                          </SidePanelActionContainer>
                          {mainPanelCollapsed ? (
                            <U21Button
                              aria-label="Show main panel"
                              icon={<IconArrowsMinimize />}
                              onClick={() => {
                                if (sideBySideEnabled) {
                                  sidePanelRef.current?.expand();
                                  mainPanelRef.current?.expand();
                                } else {
                                  sidePanelRef.current?.collapse();
                                  mainPanelRef.current?.expand();
                                }
                              }}
                            />
                          ) : (
                            <U21Button
                              aria-label="Maximize sidepanel"
                              icon={<IconArrowsMaximize />}
                              onClick={() => {
                                mainPanelRef.current?.collapse();
                                sidePanelRef.current?.expand();
                              }}
                            />
                          )}
                        </SidePanelHeaderContent>
                      )}
                    </SidePanelHeader>
                    <SidePanelContent>
                      <SidePanelMenu>
                        {list.map((Elm) => {
                          return (
                            <SidePanelMenuItem
                              key={Elm.label}
                              Elm={Elm}
                              selected={selected}
                              to={{
                                pathname,
                                search: `?${queryString.stringify({ ...parsed, [SIDEPANEL_SEARCH_PARAM_KEY]: Elm.label })}`,
                              }}
                              onClick={(e) => {
                                // don't expand sidepanel if cmd + click
                                if (!isCmdCtrlClick(e) && sidePanelCollapsed) {
                                  sidePanelRef.current?.expand();
                                  // collapse mainpanel if width too small
                                  if (!sideBySideEnabled) {
                                    mainPanelRef.current?.collapse();
                                  }
                                }
                              }}
                            />
                          );
                        })}
                      </SidePanelMenu>
                      <SidePanelMainContentContainer>
                        {selected.content}
                      </SidePanelMainContentContainer>
                    </SidePanelContent>
                  </SidePanelContainer>
                </SidePanel>
              </>
            )}
          </PanelGroup>
        )}
      </Container>
    </SidePanelInstanceContext.Provider>
  );
};

SidePanelLayout.displayName = 'SidePanelLayout';

const Container = styled.div`
  display: flex;
  width: 100%;
`;

const SidePanel = styled(Panel)<{
  $sidebarOpen?: boolean;
}>`
  position: sticky;
  top: 0;
  height: 75vh;
  display: flex;
  max-width: ${(props) => (props.$sidebarOpen ? 0 : '100%')};
`;

const Resizer = styled.div`
  width: 2px;
  height: 100%;
  background-color: transparent;
`;

const StyledPanelResizeHandle = styled(PanelResizeHandle)<{
  $dragging?: boolean;
}>`
  padding: 0 3px;

  // respect panel border-radius
  margin: ${(props) => props.theme.shape.borderRadiusMd}px 0;

  ${(props) =>
    props.$dragging &&
    css`
      ${Resizer} {
        background-color: ${props.theme.palette.grey[500_48]};
      }
    `}

  ${(props) =>
    props.disabled
      ? css``
      : css`
          &:hover ${Resizer} {
            background-color: ${props.theme.palette.grey[500_48]};
          }
        `}
`;

const SidePanelContainer = styled(Paper)<{
  $sidebarOpen?: boolean;
}>`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  transition: ${(props) => props.theme.transitions.create(['all'])};
  border-radius: ${(props) => props.theme.shape.borderRadiusMd}px;
  ${({ $sidebarOpen }) => {
    if ($sidebarOpen) {
      return css`
        width: 0;
        overflow: hidden;
      `;
    }
    return css`
      border: 1px solid ${(props) => props.theme.palette.grey[50024]};
    `;
  }}
`;

const SidePanelHeader = styled.div`
  display: flex;
  align-items: center;
  height: 60px;
`;

const CollapseButtonContainer = styled.div`
  width: ${SIDE_PANEL_MENU_WIDTH}px;
  display: flex;
  justify-content: center;
`;

const SidePanelHeaderContent = styled(U21Spacer)`
  flex: 1;
  align-items: center;
  padding: 0 16px;
`;

const SidePanelActionContainer = styled.div`
  margin-left: auto;
`;

const SidePanelContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const SidePanelMenu = styled(List)`
  height: 100%;
  width: ${SIDE_PANEL_MENU_WIDTH}px;
  background-color: ${(props) => props.theme.palette.grey[500_4]};
  border-top: 1px solid ${(props) => props.theme.palette.grey[500_24]};
  border-right: 1px solid ${(props) => props.theme.palette.grey[500_24]};
  padding: 0;
  flex-shrink: 0;
`;

const SidePanelMainContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  border-top: 1px solid ${(props) => props.theme.palette.grey[500_24]};
`;
